import axios from 'axios';
import { logger } from '@parrot-software/observability';

import type { IStoreAdminPort } from '@onboarding/application/ports';
import type { StoreAdminInfoDto } from '@onboarding/application/dtos';
import { mapStoreAdminInfoToDto } from '@onboarding/application/mappers';

const onbLog = logger.forDomain('ONB');

/**
 * Use case for fetching the store admin info snapshot.
 *
 * Flow: port → domain VO → view DTO
 * Used by the receipt step to pre-fill the fiscal form fields.
 *
 * @param props.storeAdminPort - IStoreAdminPort implementation (injected)
 * @returns Use case executor
 */
export const fetchStoreAdminInfoUseCase = (props: { storeAdminPort: IStoreAdminPort }) => {
  const { storeAdminPort } = props;

  const execute = async (): Promise<StoreAdminInfoDto> => {
    try {
      const info = await storeAdminPort.getStoreAdminInfo();
      const dto = mapStoreAdminInfoToDto(info);

      onbLog.debug('store_admin_info_fetched', {
        'onb.has_fiscal_data': !!(dto.legalName && dto.rfc),
      });

      return dto;
    } catch (error) {
      onbLog.warn('store_admin_info_fetch_failed', {
        'error.kind': (error as { constructor?: { name?: string } })?.constructor?.name ?? 'UnknownError',
        'error.message': error instanceof Error ? error.message : String(error),
        'http.status_code': axios.isAxiosError(error) ? error.response?.status : undefined,
      });
      throw error;
    }
  };

  return { execute };
};
